import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import Layout from '../../Layout';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../api';
import { toast } from 'react-toastify';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InsightsIcon from '@mui/icons-material/Insights';
import BarChartIcon from '@mui/icons-material/BarChart';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { formatDateInIST } from '../../utils/reportExport';
import { LEGAL_UPDATE_REPORT_MODES } from '../../utils/legalUpdateReportLabels';
import {
  EMPTY_FACETS,
  applyFacets,
  applySearch,
  buildFacetIndex,
  columnsForMode,
  defaultVisibleColumns,
  groupOptionsForMode,
  groupRows,
  hasActiveFacets,
  rowsToTsv,
  sortRows,
} from '../../utils/legalUpdateReportView';
import ReportTriageStrip from './components/ReportTriageStrip';
import ReportToolbar from './components/ReportToolbar';
import ReportResultsTable from './components/ReportResultsTable';
import ReportExclusionToggle from './components/ReportExclusionToggle';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

const STORAGE_KEY = 'legalUpdateReportFilters.v1';
const VIEW_STORAGE_KEY = 'legalUpdateReportView.v1';
const EXCLUDE_ORDERS_KEY = 'legalUpdateReportExcludeOrders.v1';
// The page loads the whole result set once so search, sort and grouping can run
// in memory. The backend puts no upper bound on `limit`.
const FETCH_LIMIT = 50000;

const CHART_COLORS = ['#2e7d32', '#00bfa6', '#1565c0', '#f59e0b', '#8b5cf6', '#ef4444'];
const CRITICALITY_COLORS = {
  Low: '#2e7d32',
  High: '#ef4444',
  Medium: '#f59e0b',
  Unspecified: '#94a3b8',
};
const SERVER_CRITICALITIES = ['Low', 'Medium', 'High'];

const defaultViewPrefs = (mode) => ({
  visibleColumns: defaultVisibleColumns(mode),
  density: 'compact',
  groupBy: 'none',
  rowsPerPage: 50,
  showCharts: false,
});

const readViewPrefs = (mode) => {
  const fallback = defaultViewPrefs(mode);
  try {
    const saved = JSON.parse(localStorage.getItem(VIEW_STORAGE_KEY) || '{}');
    const stored = saved?.[mode];
    if (!stored) return fallback;
    const validIds = new Set(columnsForMode(mode).map((column) => column.id));
    const visibleColumns = Array.isArray(stored.visibleColumns)
      ? stored.visibleColumns.filter((id) => validIds.has(id))
      : [];
    return {
      ...fallback,
      ...stored,
      visibleColumns: visibleColumns.length ? visibleColumns : fallback.visibleColumns,
    };
  } catch {
    return fallback;
  }
};

const LegalUpdateReportPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [filters, setFilters] = useState(() => {
    if (state?.filters) return state.filters;
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [exportMode, setExportMode] = useState(filters?.exportMode || 'complianceDeadline');

  const [allRows, setAllRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(() => new Date());
  // Server-side exclusion, so it also applies to Excel and PDF downloads.
  const [excludeOrders, setExcludeOrders] = useState(
    () => localStorage.getItem(EXCLUDE_ORDERS_KEY) === 'true'
  );

  const [search, setSearch] = useState('');
  const [facets, setFacets] = useState(EMPTY_FACETS);
  const [sort, setSort] = useState({ columnId: null, direction: null });
  const [page, setPage] = useState(0);
  const [viewPrefs, setViewPrefs] = useState(() => readViewPrefs(exportMode));
  const [exportAnchor, setExportAnchor] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [copyState, setCopyState] = useState('idle');

  const { visibleColumns, density, groupBy, rowsPerPage, showCharts } = viewPrefs;
  const isTrackerMode = exportMode === 'trackerDueDate';
  const modeMeta = LEGAL_UPDATE_REPORT_MODES[exportMode] || LEGAL_UPDATE_REPORT_MODES.complianceDeadline;

  const updateViewPrefs = useCallback(
    (patch) => setViewPrefs((prev) => ({ ...prev, ...patch })),
    []
  );

  useEffect(() => {
    if (!filters) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  // Durable display preferences persist per mode. Search and facets stay
  // transient so a returning user is never silently looking at a filtered view.
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(VIEW_STORAGE_KEY) || '{}');
      localStorage.setItem(
        VIEW_STORAGE_KEY,
        JSON.stringify({ ...saved, [exportMode]: viewPrefs })
      );
    } catch {
      /* preferences are best effort */
    }
  }, [viewPrefs, exportMode]);

  useEffect(() => {
    localStorage.setItem(EXCLUDE_ORDERS_KEY, String(excludeOrders));
  }, [excludeOrders]);

  useEffect(() => {
    const query = async () => {
      if (!filters) return;
      setLoading(true);
      try {
        const payload = { ...filters, exportMode, excludeOrders, page: 1, limit: FETCH_LIMIT };
        const { data } = await api.post('legal-update/report/query', payload);
        setAllRows(Array.isArray(data?.rows) ? data.rows : []);
        setTotal(Number(data?.total || 0));
        setNow(new Date());
      } catch (error) {
        console.error('[legal-update-report/results] query error=', error?.response?.data || error);
        toast.error(error?.response?.data?.error || 'Failed to load report data');
        setAllRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };
    query();
  }, [filters, exportMode, excludeOrders]);

  const columns = useMemo(() => columnsForMode(exportMode), [exportMode]);
  const shownColumns = useMemo(
    () => columns.filter((column) => visibleColumns.includes(column.id)),
    [columns, visibleColumns]
  );

  // Dropdown options come from the whole result set so choices never vanish
  // as the user narrows the view.
  const optionIndex = useMemo(
    () => buildFacetIndex(allRows, exportMode, now),
    [allRows, exportMode, now]
  );

  const searched = useMemo(
    () => applySearch(allRows, search, exportMode),
    [allRows, search, exportMode]
  );

  // Due-date tile counts ignore the due-date facet itself, which is what makes
  // the tiles usable as a triage switchboard rather than a dead end.
  const beforeDueFilter = useMemo(
    () => applyFacets(searched, { ...facets, dueBuckets: [] }, exportMode, now),
    [searched, facets, exportMode, now]
  );

  const filtered = useMemo(
    () =>
      (facets.dueBuckets.length
        ? applyFacets(beforeDueFilter, { dueBuckets: facets.dueBuckets }, exportMode, now)
        : beforeDueFilter),
    [beforeDueFilter, facets.dueBuckets, exportMode, now]
  );

  const tileIndex = useMemo(
    () => buildFacetIndex(beforeDueFilter, exportMode, now),
    [beforeDueFilter, exportMode, now]
  );

  const sorted = useMemo(() => sortRows(filtered, sort, exportMode), [filtered, sort, exportMode]);
  const groups = useMemo(
    () => groupRows(sorted, groupBy, exportMode, now),
    [sorted, groupBy, exportMode, now]
  );

  const chartData = useMemo(() => {
    const index = buildFacetIndex(filtered, exportMode, now);
    return {
      lawArea: index.lawAreas.slice(0, 8).map((item) => ({ name: item.value, count: item.count })),
      criticality: index.criticalities.map((item) => ({ name: item.value, value: item.count })),
    };
  }, [filtered, exportMode, now]);

  useEffect(() => {
    setPage(0);
  }, [search, facets, groupBy, sort, rowsPerPage, exportMode]);

  const handleModeChange = (mode) => {
    if (!mode || mode === exportMode) return;
    setExportMode(mode);
    setFilters((prev) => ({ ...prev, exportMode: mode }));
    setSearch('');
    setFacets(EMPTY_FACETS);
    setSort({ columnId: null, direction: null });
    setViewPrefs(readViewPrefs(mode));
    setPage(0);
  };

  const handleSortChange = (columnId) => {
    setSort((prev) => {
      if (prev.columnId !== columnId) return { columnId, direction: 'asc' };
      if (prev.direction === 'asc') return { columnId, direction: 'desc' };
      return { columnId: null, direction: null };
    });
  };

  const handleFacetChange = (key, value) =>
    setFacets((prev) => ({ ...prev, [key]: value }));

  const toggleDueBucket = (bucketId) =>
    setFacets((prev) => ({
      ...prev,
      dueBuckets: prev.dueBuckets.includes(bucketId)
        ? prev.dueBuckets.filter((id) => id !== bucketId)
        : [...prev.dueBuckets, bucketId],
    }));

  const clearView = () => {
    setSearch('');
    setFacets(EMPTY_FACETS);
    setSort({ columnId: null, direction: null });
  };

  const copyVisibleRows = async () => {
    try {
      await navigator.clipboard.writeText(rowsToTsv(sorted, shownColumns));
      setCopyState('copied');
      toast.success(`${sorted.length.toLocaleString()} rows copied. Paste into Excel.`);
      setTimeout(() => setCopyState('idle'), 2000);
    } catch {
      toast.error('Could not copy to clipboard. Use the export buttons instead.');
    }
  };

  // The server export only understands criticality and missing-date exclusions,
  // so translate those two on-screen choices into the payload it expects.
  const triggerExport = async (format, modeOverride = null) => {
    if (!filters) return;
    const requestedMode = modeOverride || exportMode;
    setExportAnchor(null);
    setExporting(requestedMode === 'merged' ? 'merged' : format);
    try {
      const selectedCriticalities = facets.criticalities;
      const excludedCriticalities = selectedCriticalities.length
        ? SERVER_CRITICALITIES.filter((value) => !selectedCriticalities.includes(value))
        : [];
      const hideNoDeadline =
        facets.dueBuckets.length > 0 && !facets.dueBuckets.includes('none');

      const response = await api.post(
        'legal-update/report/export',
        {
          ...filters,
          exportMode: requestedMode,
          format,
          excludedCriticalities,
          hideNoDeadline,
          excludeOrders,
        },
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], {
        type: format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = requestedMode === 'merged'
        ? `legal-update-merged-report-${formatDateInIST(new Date())}.xlsx`
        : isTrackerMode
          ? `legal-update-tracker-due-report-${formatDateInIST(new Date())}.${format}`
          : `legal-update-compliance-deadline-report-${formatDateInIST(new Date())}.${format}`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[legal-update-report/results] export error=', error?.response?.data || error);
      toast.error(error?.response?.data?.error || 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  const filterChips = useMemo(() => {
    if (!filters) return [];
    const chips = [];
    if (filters.issueFrom || filters.issueTo) {
      chips.push(`Issue ${filters.issueFrom || '-'} to ${filters.issueTo || '-'}`);
    }
    if (filters.deadlineFrom || filters.deadlineTo) {
      chips.push(`Deadline ${filters.deadlineFrom || '-'} to ${filters.deadlineTo || '-'}`);
    }
    if (isTrackerMode && (filters.trackerDueFrom || filters.trackerDueTo)) {
      chips.push(`Task due ${filters.trackerDueFrom || '-'} to ${filters.trackerDueTo || '-'}`);
    }
    if (Array.isArray(filters.lawArea) && filters.lawArea.includes('ALL')) chips.push('Law area: ALL');
    else if (Array.isArray(filters.lawArea) && filters.lawArea.length) {
      chips.push(`${filters.lawArea.length} law area(s)`);
    }
    if (Array.isArray(filters.departments) && filters.departments.length) {
      chips.push(`Departments: ${filters.departments.join(', ')}`);
    }
    if (filters.contentType && filters.contentType !== 'ALL') chips.push(filters.contentType);
    if (filters.criticalityLevel && filters.criticalityLevel !== 'ALL') {
      chips.push(`${filters.criticalityLevel} criticality`);
    }
    if (isTrackerMode && filters.trackerStatus && filters.trackerStatus !== 'ALL') {
      chips.push(`Status: ${filters.trackerStatus}`);
    }
    return chips;
  }, [filters, isTrackerMode]);

  if (!filters) {
    return (
      <Layout title="Legal Update Report">
        <Container sx={{ py: 4 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Filters are missing. Please select report filters first.
          </Alert>
          <Button variant="contained" onClick={() => navigate('/reports/legal-update')}>
            Go to Filters
          </Button>
        </Container>
      </Layout>
    );
  }

  const truncated = total > allRows.length;

  return (
    <Layout title="Legal Update Report Results">
      <Container maxWidth={false} sx={{ py: 2, px: { xs: 1.5, lg: 3 } }}>
        <Paper
          elevation={0}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 6,
            borderRadius: 3,
            px: { xs: 1.5, md: 2.5 },
            py: 1.5,
            mb: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'rgba(255,255,255,0.94)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={1.25}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', lg: 'center' }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <InsightsIcon color="success" />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                  {modeMeta.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {loading
                    ? 'Loading the full result set...'
                    : `${total.toLocaleString()} records - ${modeMeta.helper}`}
                </Typography>
              </Box>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
              <ToggleButtonGroup
                size="small"
                exclusive
                value={exportMode}
                onChange={(_event, value) => handleModeChange(value)}
                sx={{
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 2,
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <ToggleButton value="complianceDeadline">
                  {LEGAL_UPDATE_REPORT_MODES.complianceDeadline.title}
                </ToggleButton>
                <ToggleButton value="trackerDueDate">
                  {LEGAL_UPDATE_REPORT_MODES.trackerDueDate.title}
                </ToggleButton>
              </ToggleButtonGroup>

              <Button
                size="small"
                variant="outlined"
                startIcon={<BarChartIcon />}
                onClick={() => updateViewPrefs({ showCharts: !showCharts })}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                {showCharts ? 'Hide charts' : 'Charts'}
              </Button>

              <Button
                size="small"
                variant="contained"
                color="success"
                disabled={Boolean(exporting)}
                endIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <ArrowDropDownIcon />}
                onClick={(event) => setExportAnchor(event.currentTarget)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                {exporting ? 'Preparing...' : 'Export'}
              </Button>
              <Menu
                anchorEl={exportAnchor}
                open={Boolean(exportAnchor)}
                onClose={() => setExportAnchor(null)}
              >
                <MenuItem onClick={() => triggerExport('xlsx')}>
                  <FileDownloadIcon fontSize="small" sx={{ mr: 1, color: '#1b7f4b' }} />
                  Excel (.xlsx)
                </MenuItem>
                <MenuItem onClick={() => triggerExport('pdf')}>
                  <PictureAsPdfIcon fontSize="small" sx={{ mr: 1, color: '#c62828' }} />
                  PDF (.pdf)
                </MenuItem>
                <MenuItem onClick={() => triggerExport('xlsx', 'merged')}>
                  <FileDownloadIcon fontSize="small" sx={{ mr: 1, color: '#1b7f4b' }} />
                  Merged Export (.xlsx)
                </MenuItem>
              </Menu>

              <Button
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/reports/legal-update')}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                Filters
              </Button>
            </Stack>
          </Stack>
          {loading && <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />}
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            p: { xs: 1.5, md: 2.5 },
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'rgba(255,255,255,0.9)',
          }}
        >
          {filterChips.length > 0 && (
            <>
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                <Typography variant="caption" sx={{ fontWeight: 800, alignSelf: 'center' }}>
                  Report filters:
                </Typography>
                {filterChips.map((chip) => (
                  <Chip key={chip} label={chip} size="small" variant="outlined" />
                ))}
              </Stack>
              <Divider sx={{ my: 1.5 }} />
            </>
          )}

          {truncated && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              Showing the first {allRows.length.toLocaleString()} of {total.toLocaleString()} records.
              Narrow the report filters, or use Export for the complete set.
            </Alert>
          )}

          <ReportExclusionToggle
            checked={excludeOrders}
            onChange={setExcludeOrders}
            disabled={loading}
          />

          <ReportTriageStrip
            buckets={tileIndex.dueBuckets}
            total={tileIndex.total}
            selected={facets.dueBuckets}
            onToggle={toggleDueBucket}
            onShowAll={() => setFacets((prev) => ({ ...prev, dueBuckets: [] }))}
            dateLabel={`by ${modeMeta.dateLabel.toLowerCase()}`}
          />

          <Collapse in={showCharts} unmountOnExit>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, flex: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 800 }}>
                  Top law areas in this view
                </Typography>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={chartData.lawArea}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" hide />
                    <YAxis allowDecimals={false} />
                    <RechartsTooltip />
                    <Bar dataKey="count" fill="#2e7d32" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, flex: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 800 }}>
                  Criticality split
                </Typography>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={chartData.criticality} dataKey="value" nameKey="name" outerRadius={78}>
                      {chartData.criticality.map((entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={CRITICALITY_COLORS[entry.name] || CHART_COLORS[index % CHART_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Legend
                      formatter={(value, entry) => {
                        const count = Number(entry?.payload?.value);
                        return Number.isFinite(count) ? `${value} (${count.toLocaleString()})` : value;
                      }}
                    />
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Stack>
          </Collapse>

          <ReportToolbar
            mode={exportMode}
            search={search}
            onSearchChange={setSearch}
            facetIndex={optionIndex}
            facets={facets}
            onFacetChange={handleFacetChange}
            groupBy={groupBy}
            groupOptions={groupOptionsForMode(exportMode)}
            onGroupByChange={(value) => updateViewPrefs({ groupBy: value })}
            density={density}
            onDensityChange={(value) => updateViewPrefs({ density: value })}
            columns={columns}
            visibleColumns={visibleColumns}
            onVisibleColumnsChange={(value) => updateViewPrefs({ visibleColumns: value })}
            shownCount={sorted.length}
            totalCount={allRows.length}
            onCopyVisible={copyVisibleRows}
            onClearView={clearView}
            hasActiveView={hasActiveFacets(facets, search)}
            copyState={copyState}
          />

          {loading ? (
            <Alert severity="info">Loading report data...</Alert>
          ) : (
            <ReportResultsTable
              mode={exportMode}
              columns={shownColumns}
              rows={sorted}
              groups={groups}
              sort={sort}
              onSortChange={handleSortChange}
              density={density}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={(value) => updateViewPrefs({ rowsPerPage: value })}
              now={now}
            />
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Due status is measured against {formatDateInIST(now)} (IST) using the{' '}
            {modeMeta.dateLabel.toLowerCase()}. Search, law area, department and grouping apply to
            this screen only; use Copy visible rows to take exactly what you see into Excel.
          </Typography>
        </Paper>
      </Container>
    </Layout>
  );
};

export default LegalUpdateReportPage;
